import { getNotificationRoleSegment } from "../../util/notificationRole";

export const MAX_COMMENT_LENGTH = 50;

export const limitComment = (value) =>
  String(value || "").slice(0, MAX_COMMENT_LENGTH);

export const LAAP_FILTER_KEYS = [
  "name",
  "businessDays",
  "status",
];

const getTrackerStatusLabel = (record) =>
  record?.withdrawn ? "Withdrawn" : String(record?.status || "Approved");

const toComparableDate = (value) => normalizeCalendarDate(value);

const rowMatchesDateRange = (row, range = {}) => {
  const start = toComparableDate(row?.startDate);
  const end = toComparableDate(row?.endDate);
  const filterStart = toComparableDate(range.start);
  const filterEnd = toComparableDate(range.end);

  if (!filterStart && !filterEnd) return true;
  if (filterStart && filterEnd && filterStart > filterEnd) return false;
  if (!start || !end) return false;
  return (!filterStart || end >= filterStart) && (!filterEnd || start <= filterEnd);
};

const rowMatchesCategoricalFilter = (row, filters, key, includeName = true) => {
  if (key === "name" && !includeName) return true;
  const selectedValues = filters?.[key] || [];
  if (selectedValues.length === 0) return true;

  const rowValue = key === "status"
    ? getTrackerStatusLabel(row)
    : String(row?.[key] ?? "");
  return selectedValues.includes(rowValue);
};

export const filterLaapRows = (rows, filters = {}, includeName = true) =>
  (Array.isArray(rows) ? rows : []).filter((row) =>
    rowMatchesDateRange(row, filters.dateRange) &&
    LAAP_FILTER_KEYS.every((key) =>
      rowMatchesCategoricalFilter(row, filters, key, includeName),
    ),
  );

export const getLaapFilterOptions = (
  rows,
  filters = {},
  optionKey,
  includeName = true,
) => {
  if (optionKey === "name" && !includeName) return [];

  const otherFilters = {
    ...filters,
    [optionKey]: [],
  };
  const candidateRows = filterLaapRows(rows, otherFilters, includeName);
  const values = candidateRows
    .map((row) => optionKey === "status"
      ? getTrackerStatusLabel(row)
      : String(row?.[optionKey] ?? "").trim())
    .filter(Boolean);

  return Array.from(new Set(values)).sort((left, right) =>
    left.localeCompare(right, undefined, { numeric: true, sensitivity: "base" }),
  );
};

const normalizeCalendarDate = (value) => {
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    const year = value.getFullYear();
    const month = String(value.getMonth() + 1).padStart(2, "0");
    const day = String(value.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  }

  const text = String(value || "").trim();
  const isoDate = text.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (isoDate) {
    return `${isoDate[1]}-${isoDate[2]}-${isoDate[3]}`;
  }

  const parsedDate = new Date(text);
  return Number.isNaN(parsedDate.getTime())
    ? ""
    : normalizeCalendarDate(parsedDate);
};

// Team-member search is limited to active DID and Regional Lead users.
export const isEligibleActiveTeamMember = (user) => {
  if (user?.isActive !== true) {
    return false;
  }

  const roleValues = [
    user.roleName,
    user.roleNames,
    user.roles,
    user.userRoles,
  ].flatMap((value) => (Array.isArray(value) ? value : [value]));

  return roleValues.some((role) => {
    const roleValuesToCheck =
      typeof role === "string" ? role.split(/[,;|]/) : [role];

    return roleValuesToCheck.some((roleValue) => {
      const roleSegment = getNotificationRoleSegment(roleValue).toUpperCase();
      return roleSegment === "DID" || roleSegment === "REGIONALLEAD";
    });
  });
};

// Records remain actionable through (and including) their end date.
export const isUnavailabilityRecordActionable = (endDate, today = new Date()) => {
  const normalizedEndDate = normalizeCalendarDate(endDate);
  if (!normalizedEndDate) {
    return true;
  }
  return normalizedEndDate >= normalizeCalendarDate(today);
};

export const hasDuplicateUnavailabilityRecord = (
  records,
  { userId, email, startDate, endDate },
) => {
  const normalizedUserId = String(userId || "").trim().toLowerCase();
  const normalizedEmail = String(email || "").trim().toLowerCase();
  const normalizedStartDate = normalizeCalendarDate(startDate);
  const normalizedEndDate = normalizeCalendarDate(endDate);

  if ((!normalizedUserId && !normalizedEmail) || !normalizedStartDate || !normalizedEndDate) {
    return false;
  }

  return (Array.isArray(records) ? records : []).some((record) => {
    const recordUserId = String(record.userId || "").trim().toLowerCase();
    const recordEmail = String(record.email || "").trim().toLowerCase();
    const isSameUser =
      normalizedUserId && recordUserId
        ? normalizedUserId === recordUserId
        : Boolean(normalizedEmail && recordEmail === normalizedEmail);

    return (
      isSameUser &&
      normalizeCalendarDate(record.startDate) === normalizedStartDate &&
      normalizeCalendarDate(record.endDate) === normalizedEndDate
    );
  });
};
